import { useEffect, useId, useRef } from 'react'
import { gsap, prefersReduced } from '../lib/gsap.js'
import Button from './Button.jsx'
import useFocusTrap from './hooks/useFocusTrap.js'

export default function Modal({ onClose, title, footer, size = 'md', className = '', children }) {
  const overlayRef = useRef(null)
  const dialogRef = useRef(null)
  const closingRef = useRef(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose // always call the latest onClose (avoids stale closure in the []-dep keydown effect)
  const titleId = useId()
  useFocusTrap(dialogRef, true)

  const requestClose = () => {
    if (closingRef.current) return
    closingRef.current = true
    const done = () => onCloseRef.current()
    if (prefersReduced() || !dialogRef.current) { done(); return }
    const tl = gsap.timeline({ onComplete: done })
    tl.to(dialogRef.current, { opacity: 0, scale: 0.98, duration: 0.15, ease: 'power1.in' }, 0)
    tl.to(overlayRef.current, { opacity: 0, duration: 0.15, ease: 'power1.in' }, 0)
  }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, []) // bind once; onKey's captured requestClose reads refs + onCloseRef.current (always latest)

  return (
    <div ref={overlayRef} className="cn-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose() }}>
      <div ref={dialogRef} tabIndex={-1} className={`cn-modal cn-modal--${size} ${className}`.trim()}
        role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined}>
        <div className="cn-modal-head">
          {title && <h2 id={titleId} className="cn-modal-title">{title}</h2>}
          <Button variant="subtle" size="sm" onClick={requestClose} aria-label="Close">✕</Button>
        </div>
        <div className="cn-modal-body">{children}</div>
        {footer && <div className="cn-modal-foot">{footer}</div>}
      </div>
    </div>
  )
}
